import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Trash2, Search, LayoutList, Table2, Pencil, ChevronRight, X, CheckCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { useInventoryStore, cataloguePickerItems } from '../../../store/inventoryStore';
import { useWarehouseStore } from '../../../store/warehouseStore';
import { useAuthStore } from '../../../store/authStore';
import { useUsersStore } from '../../../store/usersStore';
import { useSuppliersStore } from '../../../store/suppliersStore';
import {
  useStockRequestStore,
  StockRequest,
  StockRequestLine,
  NewRequestLineInput,
  formatRequestRef,
  effectiveLineItemId,
  fmtRequestDay,
  isLineReceivable,
  isLineApprovable,
  isLineOrderable,
  isLineIssueable,
} from '../../../store/stockRequestStore';
import {
  getRequestTheme,
  statusBadge,
  fmtStatusLabel,
  supplierBadgeStyle,
  actionBadge,
  actionBadgeClass,
} from './theme';
import { useStockUiStore } from '../../../store/stockUiStore';
import FilterSelect from '../FilterSelect';
import RequestLineDrawer from './RequestLineDrawer';
import ExpectedDateInput from './ExpectedDateInput';
import LineActionsPanel, { SheetLineAction, LINE_ACTION_DEFS } from './LineActionsPanel';

const cellTruncate = 'overflow-hidden text-ellipsis whitespace-nowrap max-w-0';

function isSheetMenuTarget(target: EventTarget | null): boolean {
  const el = target instanceof Element
    ? target
    : (target instanceof Node ? target.parentElement : null);
  return !!el?.closest('[data-sheet-menu]');
}

/** Matches By Request order cells: content-sized or full-cell hit target, portaled menu. */
const SheetCellMenu: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  theme: ReturnType<typeof getRequestTheme>;
  isDark: boolean;
  children: React.ReactNode;
  minWidth?: number;
  /** Horizontal padding inside the cell hit area */
  padClass?: string;
  /** Fill the table cell so the whole cell is the click/hover target */
  fill?: boolean;
  /** Underline the label on hover (item name) */
  hoverUnderline?: boolean;
  /** Called when opening/choosing/dismissing so row click can be suppressed */
  onInteract?: () => void;
  /** Light up as hovered even when the pointer is on a peer selected row */
  forcedHover?: boolean;
  onHoverChange?: (hovered: boolean) => void;
}> = ({ options, value, onChange, theme, isDark, children, minWidth = 160, padClass = 'px-3', fill = false, hoverUnderline = false, onInteract, forcedHover = false, onHoverChange }) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const hoverBg = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)';

  const updateMenuPos = () => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 2;
    const menuMaxH = 192; // max-h-48
    const estimatedH = Math.min(menuMaxH, Math.max(options.length, 1) * 28 + 8);
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < estimatedH && spaceAbove > spaceBelow;

    const maxHeight = Math.max(
      80,
      Math.min(menuMaxH, openUp ? spaceAbove - 8 : spaceBelow - 8),
    );
    const top = openUp
      ? Math.max(8, rect.top - gap - maxHeight)
      : rect.bottom + gap;

    const width = Math.max(rect.width, minWidth);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }

    setMenuPos({ top, left, width, maxHeight });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
    // Re-measure after paint in case option count / font metrics differ from estimate
    const raf = requestAnimationFrame(() => {
      const menu = menuRef.current;
      const el = buttonRef.current;
      if (!menu || !el) return;
      const rect = el.getBoundingClientRect();
      const gap = 2;
      const needed = Math.min(192, menu.scrollHeight);
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const openUp = spaceBelow < needed && spaceAbove > spaceBelow;
      const maxHeight = Math.max(80, Math.min(192, openUp ? spaceAbove - 8 : spaceBelow - 8));
      const top = openUp ? Math.max(8, rect.top - gap - maxHeight) : rect.bottom + gap;
      const width = Math.max(rect.width, minWidth);
      let left = rect.left;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, window.innerWidth - width - 8);
      }
      setMenuPos({ top, left, width, maxHeight });
    });
    window.addEventListener('scroll', updateMenuPos, true);
    window.addEventListener('resize', updateMenuPos);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', updateMenuPos, true);
      window.removeEventListener('resize', updateMenuPos);
    };
  }, [open, minWidth, options.length]);

  useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;

      onInteract?.();
      setOpen(false);

      // First outside click only dismisses — don't open another row's control
      e.preventDefault();
      e.stopPropagation();

      const swallow = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
      };
      document.addEventListener('mouseup', swallow, true);
      document.addEventListener('click', swallow, true);
      window.setTimeout(() => {
        document.removeEventListener('mouseup', swallow, true);
        document.removeEventListener('click', swallow, true);
      }, 0);
    };

    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [open, onInteract]);

  const pick = (next: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onInteract?.();
    onChange(next);
    setOpen(false);
  };

  const menu = open && menuPos
    ? createPortal(
        <div
          ref={menuRef}
          className="fixed z-[80] rounded-md shadow-xl overflow-hidden overflow-y-auto"
          data-sheet-ignore-deselect
          data-sheet-menu
          style={{
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            maxHeight: menuPos.maxHeight,
            backgroundColor: theme.cardBg,
            border: `1px solid ${theme.border}`,
          }}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        >
          {options.map(o => {
            const selected = o.value === value;
            return (
              <button
                key={o.value || '__empty__'}
                type="button"
                onMouseDown={e => pick(o.value, e)}
                className="w-full text-left px-2.5 py-1.5 text-xs truncate transition-colors"
                style={{
                  color: selected ? '#60a5fa' : theme.text,
                  backgroundColor: selected ? theme.rowHover : 'transparent',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = theme.rowHover; }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = selected ? theme.rowHover : 'transparent';
                }}
              >
                {o.label || '\u00A0'}
              </button>
            );
          })}
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      {fill && <div className="min-h-[2rem] w-full" aria-hidden />}
      <button
        ref={buttonRef}
        type="button"
        onMouseDown={e => {
          e.stopPropagation();
          onInteract?.();
        }}
        onClick={e => {
          e.stopPropagation();
          onInteract?.();
          setOpen(o => !o);
        }}
        onMouseEnter={() => {
          setHovered(true);
          onHoverChange?.(true);
        }}
        onMouseLeave={e => {
          if (isSheetMenuTarget(e.relatedTarget)) return;
          setHovered(false);
          onHoverChange?.(false);
        }}
        className={`box-border text-left cursor-pointer outline-none focus-visible:ring-1 focus-visible:ring-blue-500/50 ${
          fill
            ? `absolute inset-0 flex items-stretch ${padClass}`
            : `relative inline-flex w-auto max-w-full rounded py-0.5 items-center ${padClass}`
        }`}
        style={{
          backgroundColor: hovered || open || forcedHover ? hoverBg : 'transparent',
          border: 'none',
          color: hoverUnderline
            ? ((hovered || open || forcedHover) ? '#60a5fa' : theme.text)
            : undefined,
        }}
      >
        {children}
        {hoverUnderline && (hovered || open || forcedHover) && (
          <Pencil
            size={12}
            className="flex-shrink-0 ml-1"
            style={{ color: '#60a5fa' }}
          />
        )}
      </button>
      {menu}
    </>
  );
};

const DestinationCellMenu: React.FC<{
  warehouseId: string;
  storageAreaId: string | null;
  warehouses: { id: string; name: string }[];
  storageAreasByWarehouse: Record<string, { id: string; name: string }[]>;
  onChange: (warehouseId: string, storageAreaId: string | null) => void;
  theme: ReturnType<typeof getRequestTheme>;
  isDark: boolean;
  children: React.ReactNode;
  onInteract?: () => void;
  forcedHover?: boolean;
  onHoverChange?: (hovered: boolean) => void;
}> = ({
  warehouseId,
  storageAreaId,
  warehouses,
  storageAreasByWarehouse,
  onChange,
  theme,
  isDark,
  children,
  onInteract,
  forcedHover = false,
  onHoverChange,
}) => {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [hoveredWh, setHoveredWh] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const [subPos, setSubPos] = useState<{ top: number; left: number; width: number; maxHeight: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const subRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const hoverLeaveTimer = useRef<number | null>(null);
  const hoverBg = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)';

  const clearHoverLeave = () => {
    if (hoverLeaveTimer.current != null) {
      window.clearTimeout(hoverLeaveTimer.current);
      hoverLeaveTimer.current = null;
    }
  };

  const scheduleHoverLeave = () => {
    clearHoverLeave();
    hoverLeaveTimer.current = window.setTimeout(() => {
      setHoveredWh(null);
      setSubPos(null);
    }, 160);
  };

  const placeSubmenu = (whId: string) => {
    const menuEl = menuRef.current;
    const rowEl = rowRefs.current[whId];
    if (!menuEl || !rowEl) return;
    const menuRect = menuEl.getBoundingClientRect();
    const rowRect = rowEl.getBoundingClientRect();
    const width = 180;
    const gap = 4;
    let left = menuRect.right + gap;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, menuRect.left - width - gap);
    }
    const areas = storageAreasByWarehouse[whId] ?? [];
    const estimatedH = Math.min(192, Math.max(areas.length, 1) * 28 + 8);
    const maxHeight = Math.max(80, Math.min(192, window.innerHeight - 16));
    let top = rowRect.top;
    if (top + estimatedH > window.innerHeight - 8) {
      top = Math.max(8, window.innerHeight - estimatedH - 8);
    }
    setSubPos({ top, left, width, maxHeight });
  };

  const updateMenuPos = () => {
    const el = buttonRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 2;
    const menuMaxH = 240;
    const estimatedH = Math.min(menuMaxH, Math.max(warehouses.length, 1) * 28 + 8);
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < estimatedH && spaceAbove > spaceBelow;
    const maxHeight = Math.max(
      80,
      Math.min(menuMaxH, openUp ? spaceAbove - 8 : spaceBelow - 8),
    );
    const top = openUp
      ? Math.max(8, rect.top - gap - maxHeight)
      : rect.bottom + gap;
    const width = Math.max(rect.width, 200);
    let left = rect.left;
    if (left + width > window.innerWidth - 8) {
      left = Math.max(8, window.innerWidth - width - 8);
    }
    setMenuPos({ top, left, width, maxHeight });
  };

  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      setHoveredWh(null);
      setSubPos(null);
      return;
    }
    updateMenuPos();
    const raf = requestAnimationFrame(() => updateMenuPos());
    window.addEventListener('scroll', updateMenuPos, true);
    window.addEventListener('resize', updateMenuPos);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', updateMenuPos, true);
      window.removeEventListener('resize', updateMenuPos);
    };
  }, [open, warehouses.length]);

  useLayoutEffect(() => {
    if (!open || !hoveredWh) {
      setSubPos(null);
      return;
    }
    placeSubmenu(hoveredWh);
  }, [open, hoveredWh, storageAreasByWarehouse]);

  useEffect(() => () => clearHoverLeave(), []);

  useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target)
        || menuRef.current?.contains(target)
        || subRef.current?.contains(target)
      ) return;

      onInteract?.();
      setOpen(false);
      setHoveredWh(null);

      e.preventDefault();
      e.stopPropagation();

      const swallow = (ev: Event) => {
        ev.preventDefault();
        ev.stopPropagation();
      };
      document.addEventListener('mouseup', swallow, true);
      document.addEventListener('click', swallow, true);
      window.setTimeout(() => {
        document.removeEventListener('mouseup', swallow, true);
        document.removeEventListener('click', swallow, true);
      }, 0);
    };

    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [open, onInteract]);

  const pick = (wh: string, areaId: string | null, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onInteract?.();
    onChange(wh, areaId);
    setOpen(false);
    setHoveredWh(null);
  };

  const hoveredAreas = hoveredWh ? (storageAreasByWarehouse[hoveredWh] ?? []) : [];

  const submenu = open && hoveredWh && hoveredAreas.length > 0 && subPos
    ? createPortal(
        <div
          ref={subRef}
          className="fixed z-[81] rounded-md shadow-xl overflow-hidden overflow-y-auto"
          data-sheet-ignore-deselect
          data-sheet-menu
          style={{
            top: subPos.top,
            left: subPos.left,
            width: subPos.width,
            maxHeight: subPos.maxHeight,
            backgroundColor: theme.cardBg,
            border: `1px solid ${theme.border}`,
          }}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          onMouseEnter={() => {
            clearHoverLeave();
            setHoveredWh(hoveredWh);
          }}
          onMouseLeave={scheduleHoverLeave}
        >
          {hoveredAreas.map(area => {
            const selected = hoveredWh === warehouseId && area.id === storageAreaId;
            return (
              <button
                key={area.id}
                type="button"
                onMouseDown={e => pick(hoveredWh, area.id, e)}
                className="w-full text-left px-2.5 py-1.5 text-xs truncate"
                style={{
                  color: selected ? '#60a5fa' : theme.text,
                  backgroundColor: selected ? theme.rowHover : 'transparent',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = theme.rowHover; }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = selected ? theme.rowHover : 'transparent';
                }}
              >
                {area.name}
              </button>
            );
          })}
        </div>,
        document.body,
      )
    : null;

  const menu = open && menuPos
    ? createPortal(
        <div
          ref={menuRef}
          className="fixed z-[80] rounded-md shadow-xl overflow-hidden overflow-y-auto"
          data-sheet-ignore-deselect
          data-sheet-menu
          style={{
            top: menuPos.top,
            left: menuPos.left,
            width: menuPos.width,
            maxHeight: menuPos.maxHeight,
            backgroundColor: theme.cardBg,
            border: `1px solid ${theme.border}`,
          }}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
          onMouseLeave={scheduleHoverLeave}
        >
          {warehouses.map(w => {
            const areas = storageAreasByWarehouse[w.id] ?? [];
            const hasAreas = areas.length > 0;
            const selected = w.id === warehouseId;
            const rowHovered = hoveredWh === w.id;
            return (
              <button
                key={w.id}
                ref={el => { rowRefs.current[w.id] = el; }}
                type="button"
                onMouseDown={e => {
                  if (areas.length > 1) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  pick(w.id, areas[0]?.id ?? null, e);
                }}
                onMouseEnter={() => {
                  clearHoverLeave();
                  setHoveredWh(w.id);
                }}
                className="w-full text-left px-2.5 py-1.5 text-xs flex items-center gap-1.5"
                style={{
                  color: selected ? '#60a5fa' : theme.text,
                  backgroundColor: selected || rowHovered ? theme.rowHover : 'transparent',
                }}
              >
                <span className="truncate flex-1 min-w-0">{w.name}</span>
                {hasAreas && (
                  <ChevronRight size={12} className="flex-shrink-0" style={{ color: theme.muted }} />
                )}
              </button>
            );
          })}
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onMouseDown={e => {
          e.stopPropagation();
          onInteract?.();
        }}
        onClick={e => {
          e.stopPropagation();
          onInteract?.();
          setOpen(o => !o);
        }}
        onMouseEnter={() => {
          setHovered(true);
          onHoverChange?.(true);
        }}
        onMouseLeave={e => {
          if (isSheetMenuTarget(e.relatedTarget)) return;
          setHovered(false);
          onHoverChange?.(false);
        }}
        className="relative box-border inline-flex items-center text-left cursor-pointer outline-none focus-visible:ring-1 focus-visible:ring-blue-500/50 w-full h-full min-h-[2rem] px-3"
        style={{
          backgroundColor: hovered || open || forcedHover ? hoverBg : 'transparent',
          border: 'none',
          color: theme.text,
        }}
      >
        {children}
      </button>
      {menu}
      {submenu}
    </>
  );
};

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'for_review', label: 'Awaiting Review' },
  { key: 'awaiting_order', label: 'Awaiting Order' },
  { key: 'ordered', label: 'Ordered' },
  { key: 'received', label: 'Received' },
  { key: 'draft', label: 'Draft' },
] as const;
type StatusTab = typeof STATUS_TABS[number]['key'];

type SheetRow = {
  key: string;
  line: StockRequestLine;
  request: StockRequest;
};

type LocalRow = {
  key: string;
  mode: 'catalog' | 'free_text';
  itemId: string;
  freeText: string;
  qty: number;
  supplier: string;
  expectedDate: string;
  saving: boolean;
};

const emptyLocal = (): LocalRow => ({
  key: `local-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  mode: 'catalog',
  itemId: '',
  freeText: '',
  qty: 1,
  supplier: '',
  expectedDate: '',
  saving: false,
});

function lineItemLabel(line: StockRequestLine, itemNameById: Map<string, string>): string {
  if (line.lineType === 'free_text' && !line.itemId && !line.substituteItemId) {
    return line.freeTextDescription ?? '';
  }
  const id = effectiveLineItemId(line);
  if (id && itemNameById.has(id)) return itemNameById.get(id)!;
  if (line.itemId && itemNameById.has(line.itemId)) return itemNameById.get(line.itemId)!;
  return line.freeTextDescription ?? '';
}

function lineStatusBucket(line: StockRequestLine): StatusTab | 'cancelled' {
  if (line.status === 'draft') return 'draft';
  if (line.status === 'received') return 'received';
  if (line.status === 'cancelled' || line.status === 'unavailable') return 'cancelled';
  if (line.status === 'approved') return 'awaiting_order';
  if (
    line.status === 'ordered'
    || line.status === 'partially_received'
    || line.status === 'backordered'
  ) {
    return 'ordered';
  }
  return 'for_review';
}

function matchesStatusTab(line: StockRequestLine, tab: StatusTab): boolean {
  const bucket = lineStatusBucket(line);
  if (tab === 'all') return bucket !== 'cancelled';
  return bucket === tab;
}

const SHEET_CHECK_COL_WIDTH = 40;

const SHEET_COLUMNS = [
  { key: 'sr', label: 'SR', width: 88 },
  { key: 'item', label: 'Item', width: 200 },
  { key: 'qtyDesired', label: 'Qty desired', width: 88 },
  { key: 'qtyOrdered', label: 'Ordered', width: 72 },
  { key: 'received', label: 'Received', width: 80 },
  { key: 'status', label: 'Status', width: 116 },
  { key: 'destination', label: 'Destination', width: 160 },
  { key: 'supplier', label: 'Supplier', width: 140 },
  { key: 'eta', label: 'ETA', width: 110 },
  { key: 'orderedAt', label: 'Date ordered', width: 110 },
] as const;

type SheetColKey = typeof SHEET_COLUMNS[number]['key'];
type SheetSortColumn = SheetColKey;

const DEFAULT_SHEET_COL_WIDTHS: Record<SheetColKey, number> = Object.fromEntries(
  SHEET_COLUMNS.map(col => [col.key, col.width]),
) as Record<SheetColKey, number>;

const MIN_SHEET_COL_WIDTH: Record<SheetColKey, number> = {
  item: 80,
  qtyDesired: 64,
  qtyOrdered: 52,
  received: 56,
  status: 80,
  destination: 72,
  supplier: 64,
  eta: 72,
  orderedAt: 72,
  sr: 72,
};

const SHEET_COL_WIDTHS_KEY = 'netbuilder-request-sheet-col-widths';

function loadSheetColWidths(): Record<SheetColKey, number> {
  const next = { ...DEFAULT_SHEET_COL_WIDTHS };
  try {
    const raw = localStorage.getItem(SHEET_COL_WIDTHS_KEY);
    if (!raw) return next;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    SHEET_COLUMNS.forEach(({ key }) => {
      const value = parsed[key];
      if (typeof value === 'number' && Number.isFinite(value)) {
        next[key] = Math.max(MIN_SHEET_COL_WIDTH[key], Math.round(value));
      }
    });
  } catch {
    /* keep defaults */
  }
  return next;
}

function saveSheetColWidths(widths: Record<SheetColKey, number>) {
  try {
    localStorage.setItem(SHEET_COL_WIDTHS_KEY, JSON.stringify(widths));
  } catch {
    /* ignore quota / private mode */
  }
}

function sheetColsSum(widths: Record<SheetColKey, number>): number {
  return SHEET_COLUMNS.reduce((sum, col) => sum + widths[col.key], 0);
}

/** Keep column pixels adding up to the table body so the right edge never moves. */
function fitSheetColWidths(
  widths: Record<SheetColKey, number>,
  available: number,
): Record<SheetColKey, number> {
  if (available <= 0) return widths;
  const next = { ...widths };
  const keys = SHEET_COLUMNS.map(col => col.key);
  let diff = available - sheetColsSum(next);
  if (diff === 0) return widths;

  if (diff > 0) {
    next.item += diff;
    return next;
  }

  const itemShrink = Math.min(next.item - MIN_SHEET_COL_WIDTH.item, -diff);
  next.item -= itemShrink;
  diff += itemShrink;
  if (diff === 0) return next;

  for (let i = keys.length - 1; i >= 0 && diff < 0; i--) {
    const key = keys[i];
    const take = Math.min(next[key] - MIN_SHEET_COL_WIDTH[key], -diff);
    next[key] -= take;
    diff += take;
  }
  return next;
}

type SortDirection = 'asc' | 'desc';

const compareStrings = (a: string | null | undefined, b: string | null | undefined, dir: SortDirection) => {
  const cmp = (a ?? '').localeCompare(b ?? '', undefined, { sensitivity: 'base', numeric: true });
  return dir === 'asc' ? cmp : -cmp;
};

const compareNumbers = (a: number, b: number, dir: SortDirection) =>
  dir === 'asc' ? a - b : b - a;

function dateValue(d: string | Date | null | undefined): number {
  if (!d) return 0;
  if (d instanceof Date) return d.getTime();
  const t = Date.parse(d);
  return Number.isNaN(t) ? 0 : t;
}

type SheetHoverField = 'item' | 'qty' | 'destination' | 'supplier' | 'eta';

const cellInput =
  'text-sm h-5 px-1 py-0 rounded border box-border leading-none focus:outline-none focus:ring-1 focus:ring-blue-500';
const qtyCellInput = `${cellInput} w-14 text-center`;
const dateCellInput = `${cellInput} w-[7.5rem] max-w-full`;

const cellHoverBg = (isDark: boolean) => (isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.05)');

/** Fill the sheet cell (minus a 2px inset) without growing the 2rem row. */
const sheetStatusBadgeClass =
  'flex w-full min-w-0 flex-1 justify-center items-center px-2 rounded text-xs font-semibold whitespace-nowrap leading-none box-border overflow-hidden';
const sheetSupplierBadgeClass =
  'flex w-full min-w-0 flex-1 justify-center items-center px-2 rounded-md text-xs font-semibold whitespace-nowrap leading-none box-border overflow-hidden truncate';

function sheetSupplierStyle(name: string, isDark: boolean) {
  const base = supplierBadgeStyle(name, isDark);
  return {
    backgroundColor: base.backgroundColor,
    color: base.color,
  };
}

interface Props {
  search: string;
  onSearchChange: (value: string) => void;
  isDark: boolean;
  onNewRequest?: () => void;
}

const RequestSheetTable: React.FC<Props> = ({
  search,
  onSearchChange,
  isDark,
  onNewRequest,
}) => {
  const theme = getRequestTheme(isDark);
  const filterActive = '#60a5fa';
  const { setRequestsViewMode } = useStockUiStore();
  const { user } = useAuthStore();
  const { users } = useUsersStore();
  const { items } = useInventoryStore();
  const { suppliers, fetchSuppliers } = useSuppliersStore();
  const { warehouses, storageAreasByWarehouse, defaultWarehouseId } = useWarehouseStore();
  const {
    requests,
    isLoading,
    createRequest,
    addDraftLines,
    updateSheetLine,
    removeDraftLine,
    findMyActiveDraft,
    submitRequest,
    error,
  } = useStockRequestStore();

  const [createStorageAreaId, setCreateStorageAreaId] = useState('');
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [sortColumn, setSortColumn] = useState<SheetSortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [colWidths, setColWidths] = useState<Record<SheetColKey, number>>(loadSheetColWidths);
  const [resizingCol, setResizingCol] = useState<SheetColKey | null>(null);
  const skipSortClickRef = useRef(false);
  const [filterRequester, setFilterRequester] = useState('');
  const [filterWarehouseId, setFilterWarehouseId] = useState('');
  const [localRows, setLocalRows] = useState<LocalRow[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [addingRow, setAddingRow] = useState(false);
  const [localError, setLocalError] = useState('');
  const [focusNewRow, setFocusNewRow] = useState(false);
  const [scrollToLineId, setScrollToLineId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;
  const patchTargetsRef = useRef<string[] | null>(null);
  const [hoveredLineId, setHoveredLineId] = useState<string | null>(null);
  const [hoveredField, setHoveredField] = useState<SheetHoverField | null>(null);
  const [drawerLineId, setDrawerLineId] = useState<string | null>(null);
  const [bulkAction, setBulkAction] = useState<{
    action: SheetLineAction;
    targets: { request: StockRequest; line: StockRequestLine }[];
  } | null>(null);
  const [sheetMessage, setSheetMessage] = useState<{ text: string; isError: boolean } | null>(null);
  const itemFocusRef = useRef<HTMLSelectElement | HTMLInputElement | null>(null);
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const [tableBodyWidth, setTableBodyWidth] = useState(0);
  /** Ignore row clicks briefly after inline edit UI (avoids portal click-through opening drawer) */
  const suppressDrawerUntilRef = useRef(0);
  const suppressDrawerOpen = useCallback(() => {
    suppressDrawerUntilRef.current = Date.now() + 400;
  }, []);
  const openDrawerForLine = useCallback((lineId: string) => {
    if (Date.now() < suppressDrawerUntilRef.current) return;
    setDrawerLineId(lineId);
  }, []);

  useEffect(() => { void fetchSuppliers(); }, [fetchSuppliers]);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const update = () => setTableBodyWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (tableBodyWidth <= 0) return;
    const available = tableBodyWidth - SHEET_CHECK_COL_WIDTH;
    setColWidths(prev => {
      const fitted = fitSheetColWidths(prev, available);
      return SHEET_COLUMNS.every(c => fitted[c.key] === prev[c.key]) ? prev : fitted;
    });
  }, [tableBodyWidth]);

  const createAreas = useMemo(
    () => (filterWarehouseId ? storageAreasByWarehouse[filterWarehouseId] ?? [] : []),
    [filterWarehouseId, storageAreasByWarehouse],
  );

  const storageAreaOptions = useMemo(
    () => createAreas.map(a => ({ value: a.id, label: a.name })),
    [createAreas],
  );

  useEffect(() => {
    if (!filterWarehouseId || createAreas.length === 0) {
      setCreateStorageAreaId('');
      return;
    }
    if (createStorageAreaId && !createAreas.find(a => a.id === createStorageAreaId)) {
      setCreateStorageAreaId('');
    }
  }, [filterWarehouseId, createAreas, createStorageAreaId]);

  const addWarehouseId = filterWarehouseId
    || defaultWarehouseId
    || warehouses[0]?.id
    || '';
  const addStorageAreaId = filterWarehouseId ? (createStorageAreaId || null) : null;

  const itemNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) map.set(item.id, item.name);
    return map;
  }, [items]);

  const userNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) map.set(u.id, u.displayName);
    return map;
  }, [users]);

  const whName = (id: string) => warehouses.find(w => w.id === id)?.name ?? '';
  const areaName = (whId: string, areaId: string | null) => {
    if (!areaId) return null;
    return (storageAreasByWarehouse[whId] ?? []).find(a => a.id === areaId)?.name ?? null;
  };

  const activeDraft = useMemo(
    () => (addWarehouseId ? findMyActiveDraft(addWarehouseId, addStorageAreaId) : null),
    [findMyActiveDraft, addWarehouseId, addStorageAreaId, requests],
  );

  const allRows: SheetRow[] = useMemo(() => {
    const rows: SheetRow[] = [];
    for (const req of requests) {
      if (req.status === 'cancelled') continue;
      for (const line of req.lines ?? []) {
        rows.push({ key: line.id, line, request: req });
      }
    }
    return rows;
  }, [requests]);

  const statusCounts = useMemo(() => {
    const counts: Record<StatusTab, number> = {
      all: 0,
      for_review: 0,
      awaiting_order: 0,
      ordered: 0,
      received: 0,
      draft: 0,
    };
    for (const { line } of allRows) {
      const bucket = lineStatusBucket(line);
      if (bucket === 'cancelled') continue;
      counts.all += 1;
      if (bucket !== 'all' && bucket in counts) counts[bucket] += 1;
    }
    return counts;
  }, [allRows]);

  const requesterOptions = useMemo(() => {
    const ids = new Set<string>();
    for (const { request } of allRows) ids.add(request.requesterId);
    const opts: { value: string; label: string }[] = [];
    if (user?.id) opts.push({ value: 'me', label: 'My requests' });
    Array.from(ids).forEach(id => {
      if (id === user?.id) return;
      opts.push({ value: id, label: userNameById.get(id) ?? 'Unknown' });
    });
    opts.sort((a, b) => {
      if (a.value === 'me') return -1;
      if (b.value === 'me') return 1;
      return a.label.localeCompare(b.label);
    });
    return opts;
  }, [allRows, user?.id, userNameById]);

  const warehouseOptions = useMemo(
    () => warehouses.map(w => ({ value: w.id, label: w.name })),
    [warehouses],
  );

  const sheetRows: SheetRow[] = useMemo(() => {
    let rows = allRows.filter(({ line }) => matchesStatusTab(line, statusTab));

    if (filterRequester === 'me') {
      rows = rows.filter(({ request }) => request.requesterId === user?.id);
    } else if (filterRequester) {
      rows = rows.filter(({ request }) => request.requesterId === filterRequester);
    }

    if (filterWarehouseId) {
      rows = rows.filter(({ request }) => request.warehouseId === filterWarehouseId);
      if (createStorageAreaId) {
        rows = rows.filter(({ request }) => (request.storageAreaId ?? '') === createStorageAreaId);
      }
    }

    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter(({ line, request }) => {
        const ref = formatRequestRef(request.requestNumber).toLowerCase();
        const label = lineItemLabel(line, itemNameById).toLowerCase();
        const supplier = (line.supplier ?? '').toLowerCase();
        const wh = whName(request.warehouseId).toLowerCase();
        const requester = (userNameById.get(request.requesterId) ?? '').toLowerCase();
        return (
          ref.includes(q)
          || label.includes(q)
          || supplier.includes(q)
          || wh.includes(q)
          || requester.includes(q)
          || line.status.includes(q)
        );
      });
    }

    if (sortColumn) {
      rows.sort((a, b) => {
        const dest = (row: SheetRow) => {
          const area = areaName(row.request.warehouseId, row.request.storageAreaId);
          return `${whName(row.request.warehouseId)}${area ? ` / ${area}` : ''}`;
        };
        let cmp = 0;
        switch (sortColumn) {
          case 'item':
            cmp = compareStrings(lineItemLabel(a.line, itemNameById), lineItemLabel(b.line, itemNameById), sortDirection);
            break;
          case 'qtyDesired':
            cmp = compareNumbers(a.line.requestedQty, b.line.requestedQty, sortDirection);
            break;
          case 'qtyOrdered':
            cmp = compareNumbers(a.line.orderedQty ?? 0, b.line.orderedQty ?? 0, sortDirection);
            break;
          case 'received':
            cmp = compareNumbers(a.line.receivedQty, b.line.receivedQty, sortDirection);
            break;
          case 'status':
            cmp = compareStrings(fmtStatusLabel(a.line.status), fmtStatusLabel(b.line.status), sortDirection);
            break;
          case 'destination':
            cmp = compareStrings(dest(a), dest(b), sortDirection);
            break;
          case 'supplier':
            cmp = compareStrings(a.line.supplier, b.line.supplier, sortDirection);
            break;
          case 'eta':
            cmp = compareNumbers(dateValue(a.line.expectedDate), dateValue(b.line.expectedDate), sortDirection);
            break;
          case 'orderedAt':
            cmp = compareNumbers(
              dateValue(a.line.orderedAt || a.request.orderedAt),
              dateValue(b.line.orderedAt || b.request.orderedAt),
              sortDirection,
            );
            break;
          case 'sr':
            cmp = compareNumbers(a.request.requestNumber, b.request.requestNumber, sortDirection);
            break;
          default:
            cmp = 0;
        }
        if (cmp !== 0) return cmp;
        return a.request.requestNumber - b.request.requestNumber
          || a.line.sortOrder - b.line.sortOrder
          || a.line.id.localeCompare(b.line.id);
      });
    } else {
      // Stable order so inline edits don't jump the row (avoid sorting by updatedAt).
      rows.sort((a, b) =>
        a.request.requestNumber - b.request.requestNumber
        || a.line.sortOrder - b.line.sortOrder
        || a.line.id.localeCompare(b.line.id),
      );
    }

    return rows;
  }, [
    allRows,
    statusTab,
    filterRequester,
    filterWarehouseId,
    createStorageAreaId,
    search,
    itemNameById,
    userNameById,
    user?.id,
    warehouses,
    storageAreasByWarehouse,
    sortColumn,
    sortDirection,
  ]);

  const sheetLineIds = useMemo(() => sheetRows.map(r => r.line.id), [sheetRows]);
  const selectedByAction = useMemo(() => {
    const selected = sheetRows.filter(r => selectedIds.has(r.line.id));
    return {
      approve: selected.filter(r => isLineApprovable(r.line)),
      ordered: selected.filter(r => isLineOrderable(r.line)),
      receive: selected.filter(r => isLineReceivable(r.line)),
      issue: selected.filter(r => isLineIssueable(r.line)),
    };
  }, [sheetRows, selectedIds]);

  useEffect(() => {
    setSelectedIds(prev => {
      const next = new Set<string>();
      for (const id of sheetLineIds) {
        if (prev.has(id)) next.add(id);
      }
      if (next.size !== prev.size) return next;
      for (const id of Array.from(prev)) {
        if (!next.has(id)) return next;
      }
      return prev;
    });
  }, [sheetLineIds]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds(prev =>
      prev.size === sheetLineIds.length
        ? new Set()
        : new Set(sheetLineIds),
    );
  };

  const allSelected = sheetLineIds.length > 0 && selectedIds.size === sheetLineIds.length;
  const tableColSpan = SHEET_COLUMNS.length + 1;

  const enterField = (lineId: string, field: SheetHoverField) => {
    setHoveredLineId(lineId);
    setHoveredField(field);
  };
  const leaveField = (e?: React.MouseEvent) => {
    if (isSheetMenuTarget(e?.relatedTarget ?? null)) return;
    setHoveredLineId(null);
    setHoveredField(null);
  };
  const fieldLit = (lineId: string, field: SheetHoverField) => {
    if (hoveredField !== field) return false;
    if (hoveredLineId === lineId) return true;
    return (
      selectedIds.size >= 2
      && hoveredLineId != null
      && selectedIds.has(hoveredLineId)
      && selectedIds.has(lineId)
    );
  };

  useEffect(() => {
    if (selectedIds.size === 0) return;

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      const active = document.activeElement;
      if (active instanceof HTMLInputElement && active.type === 'date') return;
      const node = e.target as Node | null;
      if (!node) return;
      const el = node instanceof Element ? node : node.parentElement;
      if (el?.closest('[data-sheet-ignore-deselect]')) return;
      if (tableScrollRef.current?.contains(node)) return;
      setSelectedIds(new Set());
    };

    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [selectedIds.size]);

  const drawerRow = useMemo(
    () => (drawerLineId ? sheetRows.find(r => r.line.id === drawerLineId) ?? null : null),
    [drawerLineId, sheetRows],
  );

  const draftLineCount = useMemo(() => {
    if (!activeDraft) return 0;
    return (activeDraft.lines ?? []).filter(l => l.status === 'draft').length;
  }, [activeDraft]);

  const addRow = useCallback(async () => {
    if (addingRow) return;
    if (!addWarehouseId) {
      setLocalError('Add a warehouse before adding items.');
      return;
    }

    setLocalError('');
    if (statusTab !== 'all' && statusTab !== 'draft') setStatusTab('draft');

    setAddingRow(true);
    const payload: NewRequestLineInput = {
      lineType: 'catalog',
      itemId: null,
      freeTextDescription: null,
      requestedQty: 1,
      condition: 'new',
    };

    try {
      const draft = findMyActiveDraft(addWarehouseId, addStorageAreaId);
      if (!draft) {
        const created = await createRequest({
          warehouseId: addWarehouseId,
          storageAreaId: addStorageAreaId,
          priority: 'medium',
          lines: [payload],
        });
        if (!created) {
          setLocalError(useStockRequestStore.getState().error ?? 'Failed to add row');
          return;
        }
        const lineId = created.lines?.[0]?.id ?? null;
        if (lineId) setScrollToLineId(lineId);
      } else {
        const added = await addDraftLines(draft.id, [payload]);
        if (!added?.[0]) {
          setLocalError(useStockRequestStore.getState().error ?? 'Failed to add row');
          return;
        }
        setScrollToLineId(added[0].id);
      }
    } finally {
      setAddingRow(false);
    }
  }, [
    addingRow,
    addWarehouseId,
    addStorageAreaId,
    statusTab,
    findMyActiveDraft,
    createRequest,
    addDraftLines,
  ]);

  useEffect(() => {
    if (!focusNewRow) return;
    itemFocusRef.current?.focus();
    setFocusNewRow(false);
  }, [focusNewRow, localRows.length]);

  useLayoutEffect(() => {
    if (!scrollToLineId) return;
    const rowEl = tableScrollRef.current?.querySelector(
      `[data-sheet-line-id="${scrollToLineId}"]`,
    ) as HTMLElement | null;
    if (!rowEl) return;
    rowEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    // Nudge up so status/destination menus have room below when near the bottom
    const scroller = tableScrollRef.current;
    if (scroller) {
      const rowRect = rowEl.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();
      const roomBelow = scrollerRect.bottom - rowRect.bottom;
      if (roomBelow < 160) {
        scroller.scrollBy({ top: 160 - roomBelow, behavior: 'smooth' });
      }
    }
    setScrollToLineId(null);
  }, [scrollToLineId, sheetRows.length]);

  const updateLocal = (key: string, patch: Partial<LocalRow>) => {
    setLocalRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)));
  };

  const removeLocal = (key: string) => {
    setLocalRows(prev => prev.filter(r => r.key !== key));
  };

  const isLocalValid = (row: LocalRow) =>
    row.qty >= 1 && (
      (row.mode === 'catalog' && row.itemId !== '')
      || (row.mode === 'free_text' && row.freeText.trim() !== '')
    );

  const persistLocalRow = async (row: LocalRow) => {
    if (!isLocalValid(row) || row.saving) return;
    if (!addWarehouseId) {
      setLocalError('Add a warehouse before adding items.');
      return;
    }

    updateLocal(row.key, { saving: true });
    setLocalError('');

    const payload: NewRequestLineInput = {
      lineType: row.mode,
      itemId: row.mode === 'catalog' ? row.itemId : null,
      freeTextDescription: row.mode === 'free_text' ? row.freeText.trim() : null,
      requestedQty: row.qty,
      condition: 'new',
    };

    try {
      const draft = findMyActiveDraft(addWarehouseId, addStorageAreaId);
      let requestId: string | null = null;
      let lineId: string | null = null;

      if (!draft) {
        const created = await createRequest({
          warehouseId: addWarehouseId,
          storageAreaId: addStorageAreaId,
          priority: 'medium',
          lines: [payload],
        });
        if (!created) {
          setLocalError(useStockRequestStore.getState().error ?? 'Failed to save row');
          updateLocal(row.key, { saving: false });
          return;
        }
        requestId = created.id;
        lineId = created.lines?.[0]?.id ?? null;
      } else {
        const added = await addDraftLines(draft.id, [payload]);
        if (!added?.[0]) {
          setLocalError(useStockRequestStore.getState().error ?? 'Failed to save row');
          updateLocal(row.key, { saving: false });
          return;
        }
        requestId = draft.id;
        lineId = added[0].id;
      }

      if (requestId && lineId && (row.supplier || row.expectedDate)) {
        await updateSheetLine(requestId, lineId, {
          supplier: row.supplier || null,
          expectedDate: row.expectedDate || null,
        });
      }

      if (lineId) setScrollToLineId(lineId);
      removeLocal(row.key);
    } catch {
      setLocalError('Failed to save row');
      updateLocal(row.key, { saving: false });
    }
  };

  const handleSubmitDraft = async () => {
    const pending = [...localRows];
    for (const row of pending) {
      if (isLocalValid(row)) await persistLocalRow(row);
    }

    const draft = findMyActiveDraft(addWarehouseId, addStorageAreaId);
    if (!draft) {
      setLocalError('Add at least one item before submitting.');
      return;
    }

    setSubmitting(true);
    setLocalError('');
    const ok = await submitRequest(draft.id);
    setSubmitting(false);
    if (!ok) {
      setLocalError(error ?? 'Failed to submit');
    } else {
      setStatusTab('for_review');
    }
  };

  const rememberPatchTargets = (lineId: string) => {
    const selected = selectedIdsRef.current;
    patchTargetsRef.current = selected.has(lineId) ? Array.from(selected) : [lineId];
  };

  const patchLine = async (
    requestId: string,
    lineId: string,
    patch: Parameters<typeof updateSheetLine>[2],
  ) => {
    setLocalError('');
    const selected = selectedIdsRef.current;
    const snapped = patchTargetsRef.current;
    const targetIds = selected.has(lineId)
      ? Array.from(selected)
      : (snapped?.includes(lineId) ? snapped : [lineId]);

    const rows = targetIds.map(id => {
      const match = sheetRows.find(r => r.line.id === id);
      if (match) return { requestId: match.request.id, lineId: id };
      if (id === lineId) return { requestId, lineId };
      return null;
    }).filter((row): row is { requestId: string; lineId: string } => row != null);

    for (const row of rows) {
      const ok = await updateSheetLine(row.requestId, row.lineId, patch);
      if (!ok) {
        setLocalError(useStockRequestStore.getState().error ?? 'Update failed');
        return;
      }
    }
  };

  const createDestinationLabel = addWarehouseId
    ? `${whName(addWarehouseId)}${
      areaName(addWarehouseId, addStorageAreaId)
        ? ` / ${areaName(addWarehouseId, addStorageAreaId)}`
        : ''
    }`
    : '';

  const [editingQtyId, setEditingQtyId] = useState<string | null>(null);

  const handleSheetSort = (column: SheetSortColumn) => {
    if (skipSortClickRef.current) {
      skipSortClickRef.current = false;
      return;
    }
    if (sortColumn === column) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const startSheetColResize = (key: SheetColKey, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const colIndex = SHEET_COLUMNS.findIndex(col => col.key === key);
    const neighbor = SHEET_COLUMNS[colIndex + 1];
    if (!neighbor) return;

    const startX = event.clientX;
    const startThis = colWidths[key];
    const startNeighbor = colWidths[neighbor.key];
    const pairTotal = startThis + startNeighbor;
    const minThis = MIN_SHEET_COL_WIDTH[key];
    const minNeighbor = MIN_SHEET_COL_WIDTH[neighbor.key];
    setResizingCol(key);
    const previousCursor = document.body.style.cursor;
    const previousSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const onMove = (moveEvent: MouseEvent) => {
      const delta = Math.round(moveEvent.clientX - startX);
      const thisWidth = Math.min(pairTotal - minNeighbor, Math.max(minThis, startThis + delta));
      const neighborWidth = pairTotal - thisWidth;
      setColWidths(prev => {
        if (prev[key] === thisWidth && prev[neighbor.key] === neighborWidth) return prev;
        const updated = { ...prev, [key]: thisWidth, [neighbor.key]: neighborWidth };
        saveSheetColWidths(updated);
        return updated;
      });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousSelect;
      skipSortClickRef.current = true;
      setResizingCol(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const colResizer = (key: SheetColKey, label: string) => (
    <button
      type="button"
      aria-label={`Resize ${label || 'column'}`}
      className={`port-advanced-col-resizer${resizingCol === key ? ' is-dragging' : ''}`}
      onMouseDown={e => startSheetColResize(key, e)}
      onClick={e => e.stopPropagation()}
    />
  );

  const getSortIcon = (column: SheetSortColumn) => {
    if (sortColumn !== column) return null;
    return sortDirection === 'asc'
      ? <ArrowUp size={12} className={isDark ? 'text-gray-300' : 'text-gray-600'} />
      : <ArrowDown size={12} className={isDark ? 'text-gray-300' : 'text-gray-600'} />;
  };

  const sortableThClass = (isActive: boolean) =>
    `px-3 py-1.5 text-left text-xs uppercase tracking-wide font-medium whitespace-nowrap cursor-pointer transition-colors ${
      isDark
        ? `hover:bg-[#2d333c] hover:text-gray-200 ${isActive ? 'text-gray-200' : ''}`
        : `hover:bg-gray-200 hover:text-gray-900 ${isActive ? 'text-gray-900' : ''}`
    }`;

  return (
    <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">
      <div className="flex items-center gap-2 px-4 pt-3 pb-0 flex-shrink-0 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
          {STATUS_TABS.map(tab => {
            const count = statusCounts[tab.key];
            const isActive = statusTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => setStatusTab(tab.key)}
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded text-xs font-medium transition-colors"
                style={{
                  backgroundColor: isActive ? (isDark ? '#374151' : '#1f2937') : 'transparent',
                  color: isActive ? '#ffffff' : theme.muted,
                  border: `1px solid ${isActive ? (isDark ? '#4b5563' : '#374151') : theme.border}`,
                }}
              >
                {tab.label}
                <span
                  className="px-1.5 py-px rounded-full text-[10px] font-semibold"
                  style={{
                    backgroundColor: isActive ? 'rgba(255,255,255,0.15)' : (isDark ? '#374151' : '#e5e7eb'),
                    color: isActive ? '#fff' : theme.muted,
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative w-52 flex-shrink-0">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: theme.muted }} />
          <input
            type="text"
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder="Search item, SR #…"
            className="w-full h-7 text-xs pl-7 pr-3 rounded border focus:outline-none focus:ring-1 focus:ring-blue-500"
            style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
          />
        </div>

        <div
          className="inline-flex items-center rounded border overflow-hidden flex-shrink-0"
          style={{ borderColor: theme.border }}
          title="How to show stock requests"
        >
          <button
            type="button"
            onClick={() => setRequestsViewMode('by_item')}
            className="inline-flex items-center gap-1 h-7 px-2 text-xs font-medium transition-colors"
            style={{
              backgroundColor: isDark ? '#1d2535' : '#eff6ff',
              color: '#60a5fa',
            }}
          >
            <Table2 size={12} />
            By item
          </button>
          <button
            type="button"
            onClick={() => setRequestsViewMode('by_request')}
            className="inline-flex items-center gap-1 h-7 px-2 text-xs font-medium transition-colors"
            style={{
              backgroundColor: 'transparent',
              color: theme.muted,
              borderLeft: `1px solid ${theme.border}`,
            }}
          >
            <LayoutList size={12} />
            By request
          </button>
        </div>

        {onNewRequest && (
          <button
            type="button"
            onClick={onNewRequest}
            className="inline-flex items-center gap-1.5 h-7 px-2.5 ml-auto bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors text-xs font-medium flex-shrink-0"
          >
            <Plus size={13} />
            New Request
          </button>
        )}
      </div>

      <div className="px-4 pt-3 pb-0 flex-shrink-0">
        <div
          className="flex items-center gap-3 flex-nowrap px-3 h-[38px] box-border rounded"
          style={{ backgroundColor: theme.cardBg, border: `1px solid ${theme.border}` }}
        >
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium" style={{ color: filterRequester ? filterActive : theme.muted }}>
              Requester
            </span>
            <FilterSelect
              value={filterRequester}
              onChange={setFilterRequester}
              placeholder="All users"
              options={requesterOptions}
              minWidth={140}
              inputBg={theme.inputBg}
              border={theme.border}
              text={theme.text}
              muted={theme.muted}
              activeColor={filterActive}
              panelBg={theme.cardBg}
              rowHover={theme.rowHover}
            />
          </div>

          <div className="w-px h-4 flex-shrink-0" style={{ backgroundColor: theme.border }} />

          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium" style={{ color: filterWarehouseId ? filterActive : theme.muted }}>
              Destination
            </span>
            <FilterSelect
              value={filterWarehouseId}
              onChange={setFilterWarehouseId}
              placeholder="All destinations"
              options={warehouseOptions}
              minWidth={140}
              inputBg={theme.inputBg}
              border={theme.border}
              text={theme.text}
              muted={theme.muted}
              activeColor={filterActive}
              panelBg={theme.cardBg}
              rowHover={theme.rowHover}
            />
          </div>

          {filterWarehouseId && createAreas.length > 0 && (
            <>
              <div className="w-px h-4 flex-shrink-0" style={{ backgroundColor: theme.border }} />

              <div className="flex items-center gap-1.5 min-w-0">
                <span className="text-xs font-medium flex-shrink-0" style={{ color: createStorageAreaId ? filterActive : theme.muted }}>
                  Storage Area
                </span>
                <FilterSelect
                  value={createStorageAreaId}
                  onChange={setCreateStorageAreaId}
                  placeholder="All storage areas"
                  options={storageAreaOptions}
                  minWidth={140}
                  inputBg={theme.inputBg}
                  border={theme.border}
                  text={theme.text}
                  muted={theme.muted}
                  activeColor={filterActive}
                  panelBg={theme.cardBg}
                  rowHover={theme.rowHover}
                />
              </div>
            </>
          )}

          <div className="ml-auto flex items-center gap-1.5 flex-shrink-0 flex-nowrap justify-end min-h-[26px]">
              {LINE_ACTION_DEFS.map(def => {
                const rows = selectedByAction[def.key];
                if (rows.length === 0) return null;
                return (
                  <button
                    key={def.key}
                    type="button"
                    onClick={() => {
                      setSheetMessage(null);
                      setBulkAction({
                        action: def.key,
                        targets: rows.map(r => ({ request: r.request, line: r.line })),
                      });
                    }}
                    className={`${actionBadgeClass} !h-[26px] !px-2.5 !text-xs ${actionBadge(def.key, isDark)}`}
                  >
                    {def.label} ({rows.length})
                  </button>
                );
              })}
              {(draftLineCount > 0 || localRows.some(isLocalValid)) && (
                <button
                  type="button"
                  onClick={() => void handleSubmitDraft()}
                  disabled={submitting || !addWarehouseId}
                  className="inline-flex items-center gap-1.5 h-[26px] px-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors text-xs font-medium disabled:opacity-50"
                >
                  {submitting
                    ? 'Submitting…'
                    : `Submit (${draftLineCount + localRows.filter(isLocalValid).length})`}
                </button>
              )}
            </div>
        </div>
        {(localError || error) && (
          <p className="text-xs text-red-400 mt-1.5">{localError || error}</p>
        )}
        {sheetMessage && (
          <p className={`text-xs mt-1.5 ${sheetMessage.isError ? 'text-red-400' : 'text-green-500'}`}>
            {sheetMessage.text}
          </p>
        )}
      </div>

      <div className="flex-1 min-h-0 min-w-0 px-4 pt-3 pb-4 flex flex-col overflow-hidden">
        <div
          ref={tableScrollRef}
          data-sheet-ignore-deselect
          className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden rounded"
          style={{ border: `1px solid ${theme.border}` }}
        >
          <table
            className="sheet-request-table"
            style={{
              width: '100%',
              maxWidth: '100%',
              ['--sheet-table-grid' as string]: theme.border,
            }}
          >
            <colgroup>
              <col style={{ width: SHEET_CHECK_COL_WIDTH }} />
              {SHEET_COLUMNS.map(col => (
                <col key={col.key} style={{ width: colWidths[col.key] }} />
              ))}
            </colgroup>
            <thead className="sticky top-0 z-10">
              <tr>
                <th
                  className="px-3 py-1.5 text-left align-middle"
                  style={{ width: SHEET_CHECK_COL_WIDTH, backgroundColor: theme.headerBg }}
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={sheetLineIds.length === 0}
                  />
                </th>
                {SHEET_COLUMNS.map((col, colIndex) => {
                  const isActive = sortColumn === col.key;
                  const canResize = colIndex < SHEET_COLUMNS.length - 1;
                  return (
                    <th
                      key={col.key}
                      onClick={() => handleSheetSort(col.key)}
                      className={sortableThClass(isActive)}
                      style={{
                        color: isActive ? theme.text : theme.muted,
                        width: colWidths[col.key],
                        backgroundColor: theme.headerBg,
                      }}
                    >
                      <div className="flex items-center gap-1 min-w-0 pr-1">
                        <span className="truncate">{col.label}</span>
                        {getSortIcon(col.key)}
                      </div>
                      {canResize && colResizer(col.key, col.label)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody style={{ backgroundColor: theme.cardBg }}>
              {isLoading && sheetRows.length === 0 && localRows.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan} className="px-3 py-10 text-center text-sm" style={{ color: theme.muted }}>
                    Loading…
                  </td>
                </tr>
              ) : sheetRows.length === 0 && localRows.length === 0 ? (
                <tr>
                  <td colSpan={tableColSpan} className="px-3 py-10 text-center text-sm" style={{ color: theme.muted }}>
                    No items yet. Use + Add row below.
                  </td>
                </tr>
              ) : (
                <>
                  {sheetRows.map((row, idx) => {
                    const { line, request } = row;
                    const label = lineItemLabel(line, itemNameById);
                    const qtyDesired = line.requestedQty;
                    const qtyOrdered = line.orderedQty;
                    const isDraft = request.status === 'draft' || line.status === 'draft';
                    const isSelected = selectedIds.has(line.id);
                    const destination = `${whName(request.warehouseId)}${
                      areaName(request.warehouseId, request.storageAreaId)
                        ? ` / ${areaName(request.warehouseId, request.storageAreaId)}`
                        : ''
                    }`;
                    const itemValue = line.lineType === 'free_text' && !line.itemId
                      ? '__free__'
                      : (line.itemId ?? '');

                    const itemOptions = [
                      { value: '', label: 'Select…' },
                      {
                        value: '__free__',
                        label: line.lineType === 'free_text' && line.freeTextDescription
                          ? line.freeTextDescription
                          : 'Free text…',
                      },
                      ...cataloguePickerItems(items, line.itemId).map(i => ({
                        value: i.id,
                        label: i.isDiscontinued ? `${i.name} (discontinued)` : i.name,
                      })),
                    ];

                    return (
                      <tr
                        key={row.key}
                        data-sheet-line-id={line.id}
                        className="group"
                        style={{
                          backgroundColor: isSelected ? (isDark ? '#1d2535' : '#eff6ff') : undefined,
                        }}
                      >
                        <td className="px-3 py-1.5 align-middle">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(line.id)}
                          />
                        </td>
                        <td
                          className={`px-3 py-1.5 text-sm font-medium align-middle cursor-pointer transition-colors ${cellTruncate}`}
                          style={{ color: theme.muted }}
                          onClick={() => openDrawerForLine(line.id)}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = cellHoverBg(isDark); }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}
                        >
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="truncate">{formatRequestRef(request.requestNumber)}</span>
                            {isDraft && (
                              <button
                                type="button"
                                onClick={e => {
                                  e.stopPropagation();
                                  if (line.status === 'draft') void removeDraftLine(request.id, line.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-900/30 flex-shrink-0"
                                style={{ color: theme.muted }}
                                title="Remove row"
                              >
                                <Trash2 size={13} />
                              </button>
                            )}
                          </div>
                        </td>
                        <td
                          className="px-3 py-1.5 text-sm align-middle min-w-0 overflow-hidden cursor-pointer"
                          onClick={() => openDrawerForLine(line.id)}
                        >
                          <SheetCellMenu
                            value={itemValue}
                            options={itemOptions}
                            theme={theme}
                            isDark={isDark}
                            minWidth={220}
                            padClass="px-1"
                            hoverUnderline
                            forcedHover={fieldLit(line.id, 'item')}
                            onHoverChange={h => { if (h) enterField(line.id, 'item'); else leaveField(); }}
                            onInteract={suppressDrawerOpen}
                            onChange={v => {
                              if (v === '__free__') {
                                void patchLine(request.id, line.id, {
                                  lineType: 'free_text',
                                  itemId: null,
                                  freeTextDescription: line.freeTextDescription || 'New item',
                                });
                              } else {
                                void patchLine(request.id, line.id, {
                                  lineType: 'catalog',
                                  itemId: v || null,
                                  freeTextDescription: null,
                                });
                              }
                            }}
                          >
                            <span
                              className="text-sm leading-none inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                              style={{ color: 'inherit' }}
                            >
                              {label || 'Select…'}
                            </span>
                          </SheetCellMenu>
                        </td>

                        <td className="p-0 text-sm whitespace-nowrap tabular-nums align-middle min-w-0">
                          {editingQtyId === line.id ? (
                            <div
                              className="flex items-center h-full min-h-[2rem] px-3 py-1.5"
                              onMouseDown={e => {
                                e.stopPropagation();
                                suppressDrawerOpen();
                              }}
                            >
                              <input
                                autoFocus
                                type="number"
                                min={1}
                                defaultValue={qtyDesired}
                                onChange={e => {
                                  const n = Math.max(1, Number(e.target.value) || 1);
                                  if (n === qtyDesired) return;
                                  const patch =
                                    isDraft && line.approvedQty == null
                                      ? { requestedQty: n }
                                      : { requestedQty: n, approvedQty: n };
                                  void patchLine(request.id, line.id, patch);
                                }}
                                onBlur={() => setEditingQtyId(null)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLInputElement).blur();
                                  }
                                  if (e.key === 'Escape') {
                                    e.preventDefault();
                                    setEditingQtyId(null);
                                  }
                                }}
                                className={qtyCellInput}
                                style={{
                                  backgroundColor: theme.inputBg,
                                  borderColor: theme.border,
                                  color: theme.text,
                                  colorScheme: isDark ? 'dark' : 'light',
                                }}
                              />
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="box-border inline-flex items-center w-full h-full min-h-[2rem] px-3 py-1.5 cursor-pointer transition-colors"
                              style={{
                                backgroundColor: fieldLit(line.id, 'qty') ? cellHoverBg(isDark) : 'transparent',
                                border: 'none',
                                color: theme.text,
                              }}
                              onMouseEnter={() => enterField(line.id, 'qty')}
                              onMouseLeave={leaveField}
                              onClick={() => {
                                suppressDrawerOpen();
                                setEditingQtyId(line.id);
                              }}
                            >
                              <span className="leading-none text-sm">{qtyDesired}</span>
                            </button>
                          )}
                        </td>

                        <td
                          className="px-3 py-1.5 text-sm whitespace-nowrap tabular-nums align-middle"
                          style={{ color: qtyOrdered ? theme.text : theme.muted }}
                        >
                          {qtyOrdered ?? ''}
                        </td>

                        <td
                          className="px-3 py-1.5 text-sm whitespace-nowrap tabular-nums align-middle"
                          style={{ color: line.receivedQty > 0 ? theme.text : theme.muted }}
                        >
                          {line.receivedQty}
                          {qtyOrdered != null && line.receivedQty > 0 && line.receivedQty < qtyOrdered ? (
                            <span style={{ color: theme.muted }}>/{qtyOrdered}</span>
                          ) : null}
                        </td>

                        <td className="p-0 relative text-sm align-middle min-w-0 overflow-hidden">
                          <div className="min-h-[2rem] w-full" aria-hidden />
                          <div className="absolute inset-0 flex items-stretch p-0.5">
                            <span className={`${sheetStatusBadgeClass} gap-1 ${statusBadge(line.status, isDark)}`}>
                              {line.status === 'received' && (
                                <CheckCircle size={12} strokeWidth={2.25} className="flex-shrink-0" aria-hidden />
                              )}
                              {fmtStatusLabel(line.status)}
                            </span>
                          </div>
                        </td>

                        <td className="p-0 text-sm align-middle min-w-0 overflow-hidden">
                          <DestinationCellMenu
                            warehouseId={request.warehouseId}
                            storageAreaId={request.storageAreaId}
                            warehouses={warehouses}
                            storageAreasByWarehouse={storageAreasByWarehouse}
                            theme={theme}
                            isDark={isDark}
                            forcedHover={fieldLit(line.id, 'destination')}
                            onHoverChange={h => { if (h) enterField(line.id, 'destination'); else leaveField(); }}
                            onInteract={suppressDrawerOpen}
                            onChange={(wh, areaId) => {
                              void patchLine(request.id, line.id, {
                                warehouseId: wh,
                                storageAreaId: areaId,
                              });
                            }}
                          >
                            <span className="truncate leading-none text-sm inline-flex items-center max-w-full" style={{ color: theme.text }} title={destination}>
                              {destination}
                            </span>
                          </DestinationCellMenu>
                        </td>

                        <td className="p-0 relative text-sm align-middle min-w-0 overflow-hidden">
                          <SheetCellMenu
                            value={line.supplier ?? ''}
                            options={[
                              { value: '', label: '' },
                              ...suppliers.map(s => ({ value: s.name, label: s.name })),
                            ]}
                            theme={theme}
                            isDark={isDark}
                            minWidth={160}
                            padClass="p-0.5"
                            fill
                            forcedHover={fieldLit(line.id, 'supplier')}
                            onHoverChange={h => { if (h) enterField(line.id, 'supplier'); else leaveField(); }}
                            onInteract={suppressDrawerOpen}
                            onChange={v => {
                              void patchLine(request.id, line.id, { supplier: v || null });
                            }}
                          >
                            {line.supplier ? (
                              <span
                                className={sheetSupplierBadgeClass}
                                style={sheetSupplierStyle(line.supplier, isDark)}
                              >
                                {line.supplier}
                              </span>
                            ) : (
                              <span className="flex flex-1 items-center text-sm leading-none" style={{ color: theme.muted }}>—</span>
                            )}
                          </SheetCellMenu>
                        </td>

                        <td className="p-0 text-sm whitespace-nowrap align-middle min-w-0">
                          <ExpectedDateInput
                            value={line.expectedDate ?? ''}
                            onChange={v => {
                              void patchLine(request.id, line.id, { expectedDate: v || null });
                            }}
                            compact
                            className="box-border px-3 py-1.5 min-h-[2rem]"
                            forceHover={fieldLit(line.id, 'eta')}
                            onHoverChange={h => { if (h) enterField(line.id, 'eta'); else leaveField(); }}
                            hoverBg={cellHoverBg(isDark)}
                            textColor={theme.text}
                            mutedColor={theme.muted}
                            onMouseDown={e => {
                              e.stopPropagation();
                              suppressDrawerOpen();
                              rememberPatchTargets(line.id);
                            }}
                          />
                        </td>

                        <td
                          className="px-3 py-1.5 text-sm whitespace-nowrap align-middle min-w-0 overflow-hidden text-ellipsis"
                          style={{ color: (line.orderedAt || request.orderedAt) ? theme.text : theme.muted }}
                          title={
                            line.orderedAt
                              ? fmtRequestDay(line.orderedAt)
                              : request.orderedAt
                                ? fmtRequestDay(request.orderedAt)
                                : undefined
                          }
                        >
                          {line.orderedAt
                            ? fmtRequestDay(line.orderedAt)
                            : request.orderedAt
                              ? fmtRequestDay(request.orderedAt)
                              : ''}
                        </td>
                      </tr>
                    );
                  })}

                  {localRows.map((row, idx) => {
                    return (
                      <tr key={row.key}>
                        <td className="px-3 py-1.5 align-middle" />
                        <td className="px-2 py-1.5 text-sm" style={{ color: theme.muted }}>
                          <div className="flex items-center justify-between gap-1 min-w-0">
                            <span className="truncate">{row.saving ? 'Saving…' : ''}</span>
                            <button
                              type="button"
                              onClick={() => removeLocal(row.key)}
                              className="p-1 rounded hover:bg-red-900/30 flex-shrink-0"
                              style={{ color: theme.muted }}
                              title="Remove row"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex items-center gap-1.5">
                            <select
                              value={row.mode}
                              onChange={e => updateLocal(row.key, {
                                mode: e.target.value as 'catalog' | 'free_text',
                                itemId: '',
                                freeText: '',
                              })}
                              className={cellInput}
                              style={{
                                backgroundColor: theme.inputBg,
                                borderColor: theme.border,
                                color: theme.text,
                                width: 72,
                                flexShrink: 0,
                              }}
                            >
                              <option value="catalog">Item</option>
                              <option value="free_text">Text</option>
                            </select>
                            {row.mode === 'catalog' ? (
                              <select
                                ref={idx === localRows.length - 1 ? (el) => { itemFocusRef.current = el; } : undefined}
                                value={row.itemId}
                                onChange={e => {
                                  const itemId = e.target.value;
                                  const next = { ...row, itemId };
                                  updateLocal(row.key, { itemId });
                                  if (isLocalValid(next)) void persistLocalRow(next);
                                }}
                                className={cellInput}
                                style={{
                                  backgroundColor: theme.inputBg,
                                  borderColor: theme.border,
                                  color: row.itemId ? theme.text : theme.muted,
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                <option value="">Select item…</option>
                                {cataloguePickerItems(items, row.itemId).map(i => (
                                  <option key={i.id} value={i.id}>{i.name}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                ref={idx === localRows.length - 1 ? (el) => { itemFocusRef.current = el; } : undefined}
                                type="text"
                                value={row.freeText}
                                placeholder="Describe item…"
                                onChange={e => updateLocal(row.key, { freeText: e.target.value })}
                                onBlur={() => {
                                  const current = localRows.find(r => r.key === row.key);
                                  if (current && isLocalValid(current)) void persistLocalRow(current);
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const current = localRows.find(r => r.key === row.key);
                                    if (current && isLocalValid(current)) void persistLocalRow(current);
                                  }
                                }}
                                className={cellInput}
                                style={{
                                  backgroundColor: theme.inputBg,
                                  borderColor: theme.border,
                                  color: theme.text,
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-1.5">
                          <input
                            type="number"
                            min={1}
                            value={row.qty}
                            onChange={e => updateLocal(row.key, { qty: Math.max(1, Number(e.target.value) || 1) })}
                            className={cellInput}
                            style={{
                              backgroundColor: theme.inputBg,
                              borderColor: theme.border,
                              color: theme.text,
                              width: 56,
                            }}
                          />
                        </td>
                        <td className="px-3 py-1.5 text-sm" style={{ color: theme.muted }} />
                        <td className="px-3 py-1.5 text-sm" style={{ color: theme.muted }} />
                        <td className="p-0 relative text-sm align-middle min-w-0 overflow-hidden">
                          <div className="min-h-[2rem] w-full" aria-hidden />
                          <div className="absolute inset-0 flex items-stretch p-0.5">
                            <span className={`${sheetStatusBadgeClass} ${statusBadge('draft', isDark)}`}>
                              {fmtStatusLabel('draft')}
                            </span>
                          </div>
                        </td>
                        <td className={`px-3 py-1.5 text-sm ${cellTruncate}`} style={{ color: theme.muted }}>
                          {createDestinationLabel}
                        </td>
                        <td className="px-3 py-1.5">
                          <select
                            value={row.supplier}
                            onChange={e => updateLocal(row.key, { supplier: e.target.value })}
                            className={cellInput}
                            style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                          >
                            <option value=""></option>
                            {suppliers.map(s => (
                              <option key={s.id} value={s.name}>{s.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-1.5">
                          <input
                            type="date"
                            value={row.expectedDate}
                            onChange={e => updateLocal(row.key, { expectedDate: e.target.value })}
                            className={cellInput}
                            style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                          />
                        </td>
                        <td className="px-3 py-1.5" />
                      </tr>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>

          <button
            type="button"
            onClick={() => void addRow()}
            disabled={addingRow || !addWarehouseId}
            className="w-full flex items-center gap-1.5 px-3 py-2 text-sm transition-colors text-left disabled:opacity-50"
            style={{
              color: theme.muted,
              borderTop: `1px solid ${theme.border}`,
              backgroundColor: theme.cardBg,
            }}
            onMouseEnter={e => {
              if (addingRow || !addWarehouseId) return;
              e.currentTarget.style.backgroundColor = theme.rowHover;
              e.currentTarget.style.color = theme.text;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = theme.cardBg;
              e.currentTarget.style.color = theme.muted;
            }}
          >
            <Plus size={14} />
            {addingRow ? 'Adding…' : 'Add row'}
          </button>
        </div>
      </div>

      {drawerRow && (
        <RequestLineDrawer
          key={drawerRow.line.id}
          request={drawerRow.request}
          line={drawerRow.line}
          isDark={isDark}
          onClose={() => {
            setDrawerLineId(null);
          }}
        />
      )}

      {bulkAction && bulkAction.targets.length > 0 && (
        <>
          <div
            className="fixed inset-0 z-40"
            style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
            onClick={() => setBulkAction(null)}
          />
          <div
            className="fixed z-50 rounded-lg shadow-2xl flex flex-col"
            data-sheet-ignore-deselect
            style={{
              top: '12%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 440,
              maxWidth: 'calc(100vw - 32px)',
              maxHeight: '76vh',
              backgroundColor: theme.cardBg,
              border: `1px solid ${theme.border}`,
            }}
          >
            <div
              className="flex items-center justify-between px-5 py-3 flex-shrink-0"
              style={{ borderBottom: `1px solid ${theme.border}` }}
            >
              <div>
                <p className="text-[11px] uppercase tracking-wide" style={{ color: theme.muted }}>
                  {LINE_ACTION_DEFS.find(d => d.key === bulkAction.action)?.label ?? 'Action'}
                </p>
                <h2 className="text-base font-semibold mt-0.5" style={{ color: theme.text }}>
                  {bulkAction.targets.length} item{bulkAction.targets.length === 1 ? '' : 's'}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setBulkAction(null)}
                className="p-1.5 rounded hover:bg-gray-700/50 transition-colors"
                style={{ color: theme.muted }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              <LineActionsPanel
                key={`${bulkAction.action}-${bulkAction.targets.map(t => t.line.id).join('|')}`}
                targets={bulkAction.targets}
                isDark={isDark}
                initialAction={bulkAction.action}
                lockAction
                onCancel={() => setBulkAction(null)}
                onComplete={message => {
                  setBulkAction(null);
                  setSelectedIds(new Set());
                  setSheetMessage({ text: message, isError: false });
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default RequestSheetTable;
